import React, {useState, useRef} from 'react';
import {Button} from 'primereact/button';
import {Dialog} from 'primereact/dialog';
import {Toast} from 'primereact/toast';
import DataTableCard from '../../../../components/ui/DataTableCard';
import {useFetchList} from '../../../../hooks/useFetchList';
import StorageHelper from '../../../../utils/storage-helper';
import "primereact/resources/themes/saga-blue/theme.css";
import "primereact/resources/primereact.min.css";
import "primeicons/primeicons.css";

import ViewMainMemberTable from "./components/ViewMainMemberTable.jsx";
import ViewSpouseMemberTable from "./components/ViewSpouseMemberTable.jsx";
import ViewMemberBeneficiaries from "./components/ViewMemberBeneficiaries.jsx";
import ViewChildBeneficiaries from "./components/ViewChildBeneficiaries.jsx";
import ViewExtendedBeneficiaryDetails from "./components/ViewExtendedBeneficiaryDetails.jsx";
import ViewBankingDetails from "./components/ViewBankingDetails.jsx";
import {approveApplication, rejectApplication} from "../../../../services/application-service";

const ApprovePolicyTable = () => {
    const {rows: policies, reload} = useFetchList('policy/list/applications');
    const [activePolicy, setActivePolicy] = useState(null);
    const [dialogPolicyVisible, setDialogPolicyVisible] = useState(false);
    const [confirmApproveVisible, setConfirmApproveVisible] = useState(false);
    const [confirmRejectVisible, setConfirmRejectVisible] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    const toast = useRef(null);

    const userRole = StorageHelper.get('userRole');
    const roleName = userRole?.name || '';
    const isAuthorized = ['ADMIN', 'UNDERWRITER', 'SUPER_ADMIN'].includes(roleName.toUpperCase());

    const status = activePolicy?.applicationStatus?.toUpperCase() || '';
    const canModify = status === 'WAITING_APPROVAL' || status === 'WAITING-APPROVAL';

    // Approves or rejects the open application; the two flows differ only in the service call, labels and dialog.
    const reviewApplication = async ({ action, doneText, failText, errorLabel, closeConfirm }) => {
        const appId = activePolicy?.policyApplicationRequestId || activePolicy?.id;
        if (!appId) {
            toast.current?.show({severity: 'error', summary: 'Error', detail: 'Application ID is missing.'});
            return;
        }

        setSubmitting(true);
        try {
            const response = await action(appId);
            toast.current?.show({severity: 'success', summary: 'Success', detail: response.data?.message || doneText});
            closeConfirm(false);
            setDialogPolicyVisible(false);
            reload();
        } catch (error) {
            console.error(errorLabel, error);
            toast.current?.show({severity: 'error', summary: 'Error', detail: error.response?.data?.message || failText});
        } finally {
            setSubmitting(false);
        }
    };

    const handleApprove = () => reviewApplication({
        action: approveApplication,
        doneText: 'Application approved successfully.',
        failText: 'Failed to approve application.',
        errorLabel: 'Error approving application:',
        closeConfirm: setConfirmApproveVisible,
    });

    const handleReject = () => reviewApplication({
        action: rejectApplication,
        doneText: 'Application rejected successfully.',
        failText: 'Failed to reject application.',
        errorLabel: 'Error rejecting application:',
        closeConfirm: setConfirmRejectVisible,
    });

    const renderOpenPolicyAction = (rowData) => {
        return (
            <div className="actions-container flex gap-2">
                <div className="tooltip" data-tip="View Full Policy">
                    <button
                        className="btn btn-sm btn-circle btn-ghost text-base-content/70 hover:text-base-content hover:bg-base-200"
                        onClick={() => {
                            setActivePolicy(rowData);
                            setDialogPolicyVisible(true);
                        }}
                    >
                        <i className="pi pi-eye text-lg"></i>
                    </button>
                </div>
            </div>
        );
    };

    const applicationStatusBodyTemplate = (rowData) => {
        const status = rowData.applicationStatus;
        if (!status) return null;
        const upperStatus = status.toUpperCase();
        let color = 'badge-ghost';
        if (upperStatus.includes('WAITING') || upperStatus === 'PENDING') {
            color = 'badge-warning';
        } else if (upperStatus === 'APPROVED' || upperStatus === 'ACTIVE' || status === 'submitted-in-easipol') {
            color = 'badge-success';
        } else if (upperStatus === 'DECLINED' || upperStatus === 'REJECTED' || status === 'rejected-policy-application') {
            color = 'badge-error';
        }
        return <span className={`badge badge-outline font-semibold text-xs uppercase ${color}`}>{status}</span>;
    };


    const cols = [
        {field: 'easipolPolicyNumber', header: 'Policy Number'},
        {field: 'mainMember.First_Name', header: 'First name'},
        {field: 'mainMember.Surname', header: 'Last name'},
        {field: 'productName', header: 'Product Name'},
        {field: 'applicationStatus', header: 'Status', body: applicationStatusBodyTemplate},
        {field: 'open-policy', header: 'Actions', body: renderOpenPolicyAction, exportable: false, style: {textAlign: 'center'}},
    ];

    return (
        <div className="w-full">
            <Toast ref={toast}/>
            <Dialog
                header="Full Policy Detail"
                visible={dialogPolicyVisible}
                maximizable
                style={{width: '60vw'}}
                onHide={() => {
                    setDialogPolicyVisible(false);
                    setActivePolicy(null);
                }}
            >
                {activePolicy && (
                    <>
                        <ViewMainMemberTable activeUserData={activePolicy}/>
                        <ViewSpouseMemberTable activeUserData={activePolicy}/>
                        <ViewChildBeneficiaries activeUserData={activePolicy}/>
                        <ViewExtendedBeneficiaryDetails activeUserData={activePolicy}/>
                        <ViewMemberBeneficiaries activeUserData={activePolicy}/>
                        <ViewBankingDetails activeUserData={activePolicy}/>

                        {isAuthorized && canModify && (
                            <div className="flex justify-content-end gap-3 mt-4 p-3 border-top-1 surface-border">
                                <Button
                                    label="Reject Application"
                                    icon="pi pi-times"
                                    severity="danger"
                                    disabled={submitting}
                                    className="btn btn-error btn-outline p-button-danger p-button-outlined"
                                    onClick={() => setConfirmRejectVisible(true)}
                                />
                                <Button
                                    label="Approve Application"
                                    icon="pi pi-check"
                                    severity="success"
                                    disabled={submitting}
                                    className="btn btn-success text-white p-button-success"
                                    onClick={() => setConfirmApproveVisible(true)}
                                />
                            </div>
                        )}
                    </>
                )}
            </Dialog>

            {/* Approve Confirmation Dialog */}
            <Dialog
                header="Confirm Approval"
                visible={confirmApproveVisible}
                style={{width: '450px'}}
                modal
                footer={
                    <div className="flex justify-content-end gap-2">
                        <Button
                            label="Cancel"
                            icon="pi pi-times"
                            className="btn btn-ghost p-button-text"
                            disabled={submitting}
                            onClick={() => setConfirmApproveVisible(false)}
                        />
                        <Button
                            label="Confirm Approval"
                            icon="pi pi-check"
                            className="btn btn-success text-white p-button-success"
                            loading={submitting}
                            onClick={handleApprove}
                        />
                    </div>
                }
                onHide={() => setConfirmApproveVisible(false)}
            >
                <div className="flex align-items-center justify-content-center p-3">
                    <i className="pi pi-exclamation-triangle text-warning mr-3" style={{fontSize: '2rem'}}/>
                    <span>Are you sure you want to approve this application?</span>
                </div>
            </Dialog>

            {/* Reject Confirmation Dialog */}
            <Dialog
                header="Confirm Rejection"
                visible={confirmRejectVisible}
                style={{width: '450px'}}
                modal
                footer={
                    <div className="flex justify-content-end gap-2">
                        <Button
                            label="Cancel"
                            icon="pi pi-times"
                            className="btn btn-ghost p-button-text"
                            disabled={submitting}
                            onClick={() => setConfirmRejectVisible(false)}
                        />
                        <Button
                            label="Confirm Rejection"
                            icon="pi pi-check"
                            className="btn btn-error text-white p-button-danger"
                            loading={submitting}
                            onClick={handleReject}
                        />
                    </div>
                }
                onHide={() => setConfirmRejectVisible(false)}
            >
                <div className="flex align-items-center justify-content-center p-3">
                    <i className="pi pi-exclamation-triangle text-danger mr-3" style={{fontSize: '2rem'}}/>
                    <span>Are you sure you want to reject this application?</span>
                </div>
            </Dialog>

            <DataTableCard
                title="Policy Applications"
                exportName="Policies"
                columns={cols}
                rows={policies}
                emptyMessage="No Policies found"
                exportDisabled={false}
            />
        </div>
    );
};

export default ApprovePolicyTable;